import { ComponentType } from '@angular/cdk/overlay';
import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CreateCompanyGroupDialogComponent } from '@app/components/company-groups/create-company-group-dialog/create-company-group-dialog.component';
import { EditCompanyGroupNameDialogComponent } from '@app/components/company-groups/edit-company-group-name-dialog/edit-company-group-name-dialog.component';
import { SelectGroupAdminDialogComponent } from '@app/components/company-groups/select-group-admin-dialog/select-group-admin-dialog.component';
import { SelectUserToCompanyGroupDialogComponent } from '@app/components/company-groups/select-users-for-company-group-dialog/select-users-for-company-group-dialog.component';
import { CreateCompanyIndustryDialogComponent } from '@app/components/company/company-industries/create-company-industry-dialog/create-company-industry-dialog.component';
import { EditCompanyIndustryDialogComponent } from '@app/components/company/company-industries/edit-company-industry-dialog/edit-company-industry-dialog.component';
import { CreateProjectDialogComponent } from '@app/components/company/company-projects/create-project-dialog/create-project-dialog.component';
import { EditProjectDialogComponent } from '@app/components/company/company-projects/edit-project-dialog/edit-project-dialog.component';
import {
  CreateCooperationDialogComponent,
  CreateCooperationDialogData
} from '@app/components/cooperations/create-cooperation-dialog/create-cooperation-dialog.component';
import { AbsenceReportDialogData } from '@app/components/dialogs/absence-report-dialog/absence-report-dialog-data';
import { AbsenceReportDialogComponent } from '@app/components/dialogs/absence-report-dialog/absence-report-dialog.component';
import type {
  CarouselDialogComponent,
  CarouselDialogData
} from '@app/components/dialogs/carousel-dialog/carousel-dialog.component';
import {
  ContractCostDialogComponent,
  ContractCostDialogData
} from '@app/components/dialogs/contract-cost-dialog/contract-cost-dialog.component';
import {
  CopyTaskDialogComponent,
  CopyTaskDialogData,
  CopyTaskDialogResult
} from '@app/components/dialogs/copy-task-dialog/copy-task-dialog.component';
import {
  CreateTaskDialogComponent,
  CreateTaskDialogData,
  CreateTaskModalResultData
} from '@app/components/dialogs/create-task-dialog/create-task-dialog.component';
import {
  EstimationDialogComponent,
  EstimationDialogData
} from '@app/components/dialogs/estimation-dialog/estimation-dialog.component';
import {
  ExportTasksDialogComponent,
  ExportTasksDialogResult
} from '@app/components/dialogs/export-tasks-dialog/export-tasks-dialog.component';
import {
  FileExplorerExternalLinkDialogComponent,
  FileExplorerExternalLinkDialogData,
  FileExplorerExternalLinkDialogResult
} from '@app/components/dialogs/file-explorer-external-link-dialog/file-explorer-external-link-dialog.component';
import {
  FileExplorerMoveDialogComponent,
  FileExplorerMoveDialogData
} from '@app/components/dialogs/file-explorer-move-dialog/file-explorer-move-dialog.component';
import {
  FileExplorerNameDialogComponent,
  FileExplorerNameDialogData
} from '@app/components/dialogs/file-explorer-name-dialog/file-explorer-name-dialog.component';
import {
  FinalCostDialogComponent,
  FinalCostDialogData
} from '@app/components/dialogs/final-cost-dialog/final-cost-dialog.component';
import {
  RequireDescriptionAcknowledgementAgainDialogComponent,
  RequireDescriptionAcknowledgementAgainDialogResult
} from '@app/components/dialogs/require-description-acknowledgement-again-dialog/require-description-acknowledgement-again-dialog.component';
import {
  SelectCooperationProjectsDialogComponent,
  SelectCooperationProjectsDialogData
} from '@app/components/dialogs/select-cooperation-projects-dialog/select-cooperation-projects-dialog.component';
import { SelectGroupsDialogData } from '@app/components/dialogs/select-groups-dialog/select-groups-dialog-data';
import { SelectGroupsDialogComponent } from '@app/components/dialogs/select-groups-dialog/select-groups-dialog.component';
import { SelectIndustryDialogComponent } from '@app/components/dialogs/select-industry-dialog/select-industry-dialog.component';
import {
  MultipleProjectParticipantsDialogData,
  MultipleProjectParticipantsResultData,
  SelectMultipleProjectParticipantsDialogComponent
} from '@app/components/dialogs/select-multiple-project-participants-dialog/select-multiple-project-participants-dialog.component';
import { SelectParentTaskDialogData } from '@app/components/dialogs/select-parent-task-dialog/select-parent-task-dialog-data';
import { SelectParentTaskDialogComponent } from '@app/components/dialogs/select-parent-task-dialog/select-parent-task-dialog.component';
import { SelectSingleLocationDialogComponent } from '@app/components/dialogs/select-single-location-dialog/select-single-location-dialog.component';
import {
  SelectTagsDialogComponent,
  SelectTagsDialogData
} from '@app/components/dialogs/select-tags-dialog/select-tags-dialog.component';
import {
  SelectUserDialogComponent,
  SelectUserDialogData
} from '@app/components/dialogs/select-user-dialog/select-user-dialog.component';
import {
  UserRoleFilterDialogComponent as SelectUserRoleDialogComponent,
  SelectUserRoleDialogData
} from '@app/components/dialogs/select-user-role-dialog/select-user-role-dialog.component';
import {
  SelectCompanyUsersDialogData,
  SelectUsersDialogComponent,
  SelectUsersDialogData
} from '@app/components/dialogs/select-users-dialog/select-users-dialog.component';
import {
  TagFormDialogComponent,
  TagFormDialogResult
} from '@app/components/dialogs/tag-form-dialog/tag-form-dialog.component';
import { TaskAdvancedFiltersDialogComponent } from '@app/components/dialogs/task-advanced-filters-dialog/task-advanced-filters-dialog.component';
import { TimeReportTypeSelectorDialogComponent } from '@app/components/dialogs/time-report-type-selector-dialog/time-report-type-selector-dialog.component';
import { AddOshAnalysisDialogComponent } from '@app/components/osh/add-osh-analysis-dialog/add-osh-analysis-dialog.component';
import { OshSelectUsersDialogComponent } from '@app/components/osh/osh-select-users-dialog/osh-select-users-dialog.component';
import { SelectIndustryAdminDialogComponent } from '@app/components/project-industries/select-industry-admin-dialog/select-industry-admin-dialog.component';
import { SelectUsersAsProjectParticpantsDialogComponent } from '@app/components/project-participants/select-users-as-project-participants-dialog/select-users-as-project-particpants-dialog.component';
import { AddMultipleSublocationDialogComponent } from '@app/components/project/project-locations/add-multiple-sublocation-dialog/add-multiple-sublocation-dialog.component';
import { AddSublocationDialogComponent } from '@app/components/project/project-locations/add-sublocation-dialog/add-sublocation-dialog.component';
import { ChangeLocationParentDialogComponent } from '@app/components/project/project-locations/change-location-parent-dialog/change-location-parent-dialog.component';
import { DuplicateLocationDialogComponent } from '@app/components/project/project-locations/duplicate-location-dialog/duplicate-location-dialog.component';
import { EditLocationDialogComponent } from '@app/components/project/project-locations/edit-location-dialog/edit-location-dialog.component';
import { TaskDetailsPdfDialogComponent } from '@app/components/task/task-details-pdf-dialog/task-details-pdf-dialog.component';
import { ManualHoursTimeReportDialogComponent } from '@app/components/time-reports/manual-hours-time-report-dialog/manual-hours-time-report-dialog.component';
import { TimeReportDialogComponent } from '@app/components/time-reports/time-report-dialog/time-report-dialog.component';
import { ChangeUserPasswordDialogComponent } from '@app/components/user/change-user-password-dialog/change-user-password-dialog.component';
import {
  CreateUserDialogComponent,
  CreateUserDialogData
} from '@app/components/user/create-user-dialog/create-user-dialog.component';
import {
  EditUserDialogComponent,
  UserEditDialogData
} from '@app/components/user/edit-user-dialog/edit-user-dialog.component';
import { ConfirmationDialogComponent } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { InformationDialogComponent } from '@app/shared/components/information-dialog/information-dialog.component';
import { TimeReportsActionsService } from '@app/shared/services/actions/time-reports-actions.service';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { AuthService } from '@app/shared/services/auth.service';
import {
  CompanyIndustryEditDialogData,
  CreateCompanyIndustryDialogData
} from '@app/shared/services/dtos/company-industry/company-industry.dto';
import { CreateCooperationRequest } from '@app/shared/services/dtos/cooperation/create-cooperation-request.dto';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { FilePreviewDialogService } from '@app/shared/services/shared/file-preview-dialog.service';
import { openTrackedDialog } from '@app/shared/services/shared/tracked-dialog';
import { UsersDataService } from '@app/shared/services/users-data.service';
import {
  CreateCompanyGroupDialogData,
  EditCompanyGroupNameDialogData,
  Group,
  SelectAdminGroupDialogData,
  SelectUsersAsProjectParticpantsDialogData,
  SelectUsersToCompanyGroupDialogData
} from '@app/shared/types/models/group/group.model';
import { Industry } from '@app/shared/types/models/industry/industry.model';
import {
  AddProjectLocationDialogData,
  ChangeLocationParentDialogData,
  DuplicateLocationsDialogData,
  Location,
  ProjectLocationEditDialogData,
  SelectSingleLocationDialogData
} from '@app/shared/types/models/location/location.model';
import {
  AddOshAnalysisDialogData,
  CreateOrEditOshAnalysisDialogData,
  EditOshAnalysisDialogData,
  SelectUsersToOshAnalysisDialogData
} from '@app/shared/types/models/osh/osh.model';
import { CompanyUsersPageParams } from '@app/shared/types/models/page-params';
import {
  SelectIndustryAdminDialogData,
  SelectIndustryAdminModalRequest,
  SelectIndustryDialogData
} from '@app/shared/types/models/project-industry/project-industry.model';
import {
  Project,
  ProjectEditDialogData,
  SelectProjectSupervisorDialogData
} from '@app/shared/types/models/project/project.model';
import { TimeReport, TimeReportType } from '@app/shared/types/models/reports/time-report';
import { BasicDialogData } from '@app/shared/types/models/shared/confirmation-dialog.model';
import { Tag, TagFormDialogData } from '@app/shared/types/models/tag/tag.model';
import { TaskContractCost } from '@app/shared/types/models/task/task-contract-cost';
import { TaskEstimation } from '@app/shared/types/models/task/task-estimation';
import { TaskUserRoleFilter } from '@app/shared/types/models/task/task-filters.model';
import { TaskFinalCost } from '@app/shared/types/models/task/task-final-cost';
import { Task } from '@app/shared/types/models/task/task.model';
import { User } from '@app/shared/types/models/user/user.model';

/**
 * Opens application dialogs and centralizes their shared configuration.
 *
 * Scope: Global singleton (`providedIn: 'root'`).
 *
 * Usage: Used by feature components and action services to open typed dialogs.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly matDialog: MatDialog = inject(MatDialog);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly filePreviewDialogService = inject(FilePreviewDialogService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly usersDataService: UsersDataService = inject(UsersDataService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly timeReportsActionsService = inject(TimeReportsActionsService);

  public openChangePasswordDialog(): void {
    this.openDialog(ChangeUserPasswordDialogComponent, { width: '400px' });
  }

  public openCreateUserDialog(params: CompanyUsersPageParams): MatDialogRef<CreateUserDialogComponent, User> {
    const data: CreateUserDialogData = { pageParams: params };
    return this.openDialog(CreateUserDialogComponent, { data, width: '600px' });
  }

  public openEditUserDialog(user: User): void {
    const data: UserEditDialogData = { userToEdit: user };
    this.openDialog(EditUserDialogComponent, { data, width: '600px' });
  }

  public openFileExplorerMoveDialog(
    data: FileExplorerMoveDialogData
  ): MatDialogRef<FileExplorerMoveDialogComponent, string> {
    return this.openDialog(FileExplorerMoveDialogComponent, { data, width: '640px' });
  }

  public openFileExplorerExternalLinkDialog(
    data: FileExplorerExternalLinkDialogData
  ): MatDialogRef<FileExplorerExternalLinkDialogComponent, FileExplorerExternalLinkDialogResult> {
    return this.openDialog(FileExplorerExternalLinkDialogComponent, { data, width: '520px' });
  }

  public openFileExplorerNameDialog(
    data: FileExplorerNameDialogData
  ): MatDialogRef<FileExplorerNameDialogComponent, string> {
    return this.openDialog(FileExplorerNameDialogComponent, { data, width: '440px' });
  }

  public openConfirmationDialog(data: BasicDialogData): MatDialogRef<ConfirmationDialogComponent, boolean> {
    return this.openDialog(ConfirmationDialogComponent, { data, autoFocus: 'dialog' });
  }

  public openInformationDialog(data: BasicDialogData): MatDialogRef<InformationDialogComponent> {
    return this.openDialog(InformationDialogComponent, { data });
  }

  public openRequireDescriptionAcknowledgementAgainDialog(): MatDialogRef<
    RequireDescriptionAcknowledgementAgainDialogComponent,
    RequireDescriptionAcknowledgementAgainDialogResult
  > {
    return this.openDialog(RequireDescriptionAcknowledgementAgainDialogComponent, { autoFocus: 'dialog' });
  }

  public openCreateCompanyIndustryDialog(params: CompanyUsersPageParams): void {
    const data: CreateCompanyIndustryDialogData = { pageParams: params };
    this.openDialog(CreateCompanyIndustryDialogComponent, { data, width: '400px' });
  }

  public openCreateCompanyProjectDialog(): void {
    this.openDialog(CreateProjectDialogComponent, { width: '1000px' });
  }

  public openEditCompanyProjectDialog(
    project: Project,
    isDraftList: boolean
  ): MatDialogRef<EditProjectDialogComponent> {
    const data: ProjectEditDialogData = {
      projectToEdit: project,
      isDraftList: isDraftList
    };
    return this.openDialog(EditProjectDialogComponent, {
      data: { ...data }
    });
  }

  public openEditCompanyIndustryDialog(companyIndustry: Industry): void {
    const data: CompanyIndustryEditDialogData = { companyIndustryToEdit: companyIndustry };
    this.openDialog(EditCompanyIndustryDialogComponent, {
      data: { ...data },
      width: '400px'
    });
  }

  public openEditProjectLocationDialog(location: Location): void {
    const data: ProjectLocationEditDialogData = { projectLocationToEdit: location };
    this.openDialog(EditLocationDialogComponent, {
      data,
      width: '400px'
    });
  }

  public openAddProjectLocationDialog(projectId: string, location?: Location): void {
    const data: AddProjectLocationDialogData = { parentId: location?.id, projectId };
    this.openDialog(AddSublocationDialogComponent, { data, width: '400px' });
  }

  public openAddMultipleLocationDialog(projectId: string, location?: Location): void {
    const data: AddProjectLocationDialogData = { parentId: location?.id, projectId };
    this.openDialog(AddMultipleSublocationDialogComponent, { data, width: '400px' });
  }

  public openTagFormDialog(data: TagFormDialogData): MatDialogRef<TagFormDialogComponent, TagFormDialogResult> {
    return this.openDialog(TagFormDialogComponent, { data, width: '400px' });
  }

  public openCreateCompanyGroupDialog(parentGroupId: string): void {
    const data: CreateCompanyGroupDialogData = { parentGroupId: parentGroupId };
    this.openDialog(CreateCompanyGroupDialogComponent, { data, width: '400px' });
  }

  public openCreateCooperationDialog(
    data: CreateCooperationDialogData
  ): MatDialogRef<CreateCooperationDialogComponent, CreateCooperationRequest> {
    return this.matDialog.open(CreateCooperationDialogComponent, { data, width: '500px' });
  }

  public openSelectCooperationProjectsDialog(
    data: SelectCooperationProjectsDialogData
  ): MatDialogRef<SelectCooperationProjectsDialogComponent, string[]> {
    return this.matDialog.open(SelectCooperationProjectsDialogComponent, { data, width: '500px' });
  }

  public openSelectCompanyUsersDialog(
    data: SelectCompanyUsersDialogData
  ): MatDialogRef<SelectUsersDialogComponent, string[]> {
    return this.matDialog.open(SelectUsersDialogComponent, {
      data: {
        titleKey: 'cooperations.modals.select-company-users-title',
        users: data.users,
        preselectedUserIds: data.preselectedUserIds,
        emptyMessageKey: 'cooperations.no-company-users-available',
        returnIdsOnly: true
      } satisfies SelectUsersDialogData,
      width: '600px'
    });
  }

  public openSelectUsersToCompanyGroupDialog(preselectedUsers: User[], lockedUser: User, group: Group): void {
    const data: SelectUsersToCompanyGroupDialogData = {
      preselectedUsers: preselectedUsers,
      group: group,
      lockedUser: lockedUser
    };
    this.openDialog(SelectUserToCompanyGroupDialogComponent, { data, width: '600px' });
  }

  public openSelectUsersAsProjectParticpantsDialog(preselectedUsers: User[], lockedUser: User, group: Group): void {
    const data: SelectUsersAsProjectParticpantsDialogData = {
      preselectedUsers: preselectedUsers,
      group: group,
      lockedUser: lockedUser
    };
    this.openDialog(SelectUsersAsProjectParticpantsDialogComponent, { data, width: '600px' });
  }

  public opensSelectGroupAdminDialog(preselectedUser: User, group: Group, isProjectParticipantsView = false): void {
    const data: SelectAdminGroupDialogData = {
      preselectedUser: preselectedUser,
      id: group.id,
      group: group,
      isProjectParticipantsView
    };
    this.openDialog(SelectGroupAdminDialogComponent, { data, width: '600px' });
  }

  public opensSelectIndustryAdminDialog(
    preselectedUser: User
  ): MatDialogRef<SelectIndustryAdminDialogComponent, SelectIndustryAdminModalRequest> {
    const data: SelectIndustryAdminDialogData = {
      preselectedUser: preselectedUser
    };
    return this.openDialog(SelectIndustryAdminDialogComponent, { data, width: '400px' });
  }

  public openSelectIndustryDialog(
    data: SelectIndustryDialogData
  ): MatDialogRef<SelectIndustryDialogComponent, Industry | Industry[]> {
    return this.openDialog(SelectIndustryDialogComponent, {
      data,
      width: this.deviceService.isMobile() ? '95vw' : '600px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '600px'
    });
  }
  public openTimeReportDialog(
    task?: Task,
    timeReport?: TimeReport,
    correlationId?: string,
    timeReportType: TimeReportType = 'WorkTime',
    // for refreshing reports store after adding new report
    dateFrom?: Date,
    dateTo?: Date
  ): MatDialogRef<TimeReportDialogComponent, Task> {
    const dialogComponent: ComponentType<TimeReportDialogComponent> = this.shouldOpenManualHoursTimeReportDialog(
      timeReportType
    )
      ? ManualHoursTimeReportDialogComponent
      : TimeReportDialogComponent;

    return this.openDialog(dialogComponent, {
      panelClass: 'task-dialog',
      width: this.deviceService.isMobile() ? '95vw' : '600px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '600px',
      height: 'auto',
      maxHeight: '95vh',
      data: {
        timeReport,
        task,
        project: task?.project(),
        correlationId: correlationId,
        timeReportType: timeReportType,
        dateFrom,
        dateTo,
        selectUser: (companyId: string, preselectedUserId?: string) =>
          this.openSelectReportUserDialog({
            titleKey: 'company-projects.modals.select-user-modal-title',
            companyId,
            preselectedUserId
          }).afterClosed()
      }
    });
  }

  public openAbsenceReportDialog(data: AbsenceReportDialogData): MatDialogRef<AbsenceReportDialogComponent, boolean> {
    return this.openDialog(AbsenceReportDialogComponent, {
      data: {
        ...data,
        selectUser: (companyId: string, preselectedUserId?: string) =>
          this.openSelectReportUserDialog({
            titleKey: 'company-projects.modals.select-user-modal-title',
            companyId,
            preselectedUserId
          }).afterClosed()
      } satisfies AbsenceReportDialogData,
      width: this.deviceService.isMobile() ? '95vw' : '600px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '600px',
      height: 'auto',
      maxHeight: '95vh'
    });
  }

  public openTimeReportTypeSelectorDialog(): MatDialogRef<TimeReportTypeSelectorDialogComponent, TimeReportType> {
    return this.openDialog(TimeReportTypeSelectorDialogComponent, {
      width: this.deviceService.isMobile() ? '95vw' : '400px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '400px',
      height: 'auto',
      autoFocus: 'dialog'
    });
  }

  public openTimerDialog(task: Task): MatDialogRef<TimeReportDialogComponent, boolean> {
    return this.openDialog(TimeReportDialogComponent, {
      panelClass: 'task-dialog',
      width: this.deviceService.isMobile() ? '95vw' : '600px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '600px',
      height: 'auto',
      maxHeight: '95vh',
      data: {
        task,
        onlyTimerMode: true
      }
    });
  }

  public openGenerateTaskPdfDialog(task: Task): MatDialogRef<TaskDetailsPdfDialogComponent, Task> {
    return this.openDialog(TaskDetailsPdfDialogComponent, {
      panelClass: 'task-dialog',
      width: this.deviceService.isMobile() ? '95vw' : '800px',
      height: 'auto',
      maxHeight: '95vh',
      data: {
        task
      }
    });
  }

  public openEditCompanyGroupNameDialog(group: Group): void {
    const data: EditCompanyGroupNameDialogData = { group: group, id: group.id };
    this.openDialog(EditCompanyGroupNameDialogComponent, { data, width: '400px' });
  }

  public openSelectProjectSupervisorDialog(
    data: SelectProjectSupervisorDialogData
  ): MatDialogRef<SelectUserDialogComponent, User | undefined> {
    const companyId = this.activeCompanyService.activeCompany()?.id;
    return this.openDialog(SelectUserDialogComponent, {
      data: {
        titleKey: 'company-projects.modals.select-supervisor-modal-title',
        users: this.usersDataService.getCompanyUsers(companyId)(),
        preselectedUserId: data.preselectedUser?.id
      } satisfies SelectUserDialogData,
      width: '600px'
    });
  }

  public openSelectReportUserDialog(
    data: SelectUserDialogData
  ): MatDialogRef<SelectUserDialogComponent, User | undefined> {
    return this.openDialog(SelectUserDialogComponent, {
      data,
      width: '600px'
    });
  }

  public openChangeProjectLocationParentDialog(data: ChangeLocationParentDialogData): void {
    this.openDialog(ChangeLocationParentDialogComponent, { data, width: '600px' });
  }

  public openDuplicateProjectLocationDialog(data: DuplicateLocationsDialogData): void {
    this.openDialog(DuplicateLocationDialogComponent, { data, width: '600px' });
  }

  public openSelectGroupsDialog(
    data: SelectGroupsDialogData
  ): MatDialogRef<SelectGroupsDialogComponent, Group[] | undefined> {
    return this.matDialog.open(SelectGroupsDialogComponent, { data, width: '800px' });
  }

  public openSelectParentTaskDialog(
    data: SelectParentTaskDialogData
  ): MatDialogRef<SelectParentTaskDialogComponent, boolean | undefined> {
    return this.openDialog(SelectParentTaskDialogComponent, {
      data,
      width: this.deviceService.isMobile() ? '95vw' : '700px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '700px',
      maxHeight: '90vh'
    });
  }

  public openSelectUserRoleDialog(
    data: SelectUserRoleDialogData
  ): MatDialogRef<SelectUserRoleDialogComponent, TaskUserRoleFilter[]> {
    return this.openDialog(SelectUserRoleDialogComponent, { data, width: '400px' });
  }

  public openSelectSingleLocationDialog(
    data: SelectSingleLocationDialogData
  ): MatDialogRef<SelectSingleLocationDialogComponent, Location | undefined> {
    return this.openDialog(SelectSingleLocationDialogComponent, { data, width: '600px' });
  }

  public openEstimationDialog(
    data: EstimationDialogData
  ): MatDialogRef<EstimationDialogComponent, TaskEstimation | undefined> {
    return this.openDialog(EstimationDialogComponent, { data, width: '480px' });
  }

  public openFinalCostDialog(
    data: FinalCostDialogData
  ): MatDialogRef<FinalCostDialogComponent, TaskFinalCost | undefined> {
    return this.openDialog(FinalCostDialogComponent, { data, width: '480px' });
  }

  public openContractCostDialog(
    data: ContractCostDialogData
  ): MatDialogRef<ContractCostDialogComponent, TaskContractCost | undefined> {
    return this.openDialog(ContractCostDialogComponent, { data, width: '480px' });
  }

  public openSelectMultipleProjectParticipantsDialog(
    data: MultipleProjectParticipantsDialogData
  ): MatDialogRef<SelectMultipleProjectParticipantsDialogComponent, MultipleProjectParticipantsResultData | undefined> {
    return this.openDialog(SelectMultipleProjectParticipantsDialogComponent, {
      data,
      width: '500px'
    });
  }

  public openSelectTagsDialog(data: SelectTagsDialogData): MatDialogRef<SelectTagsDialogComponent, Tag[]> {
    return this.openDialog(SelectTagsDialogComponent, { data, width: '400px' });
  }

  public openCreateOshAnalysisDialog(
    data: AddOshAnalysisDialogData
  ): MatDialogRef<AddOshAnalysisDialogComponent, CreateOrEditOshAnalysisDialogData> {
    return this.openDialog(AddOshAnalysisDialogComponent, { data });
  }

  public selectUsersToOshAnalysis(
    preselectedUsers: User[],
    lockedUser: User
  ): MatDialogRef<
    OshSelectUsersDialogComponent,
    {
      usersToRemove: User[];
      usersToAdd: User[];
    }
  > {
    const data: SelectUsersToOshAnalysisDialogData = {
      preselectedUsers: preselectedUsers,
      lockedUser: lockedUser
    };
    return this.openDialog(OshSelectUsersDialogComponent, { data });
  }

  public openEditOshAnalysisDialog(
    data: EditOshAnalysisDialogData
  ): MatDialogRef<AddOshAnalysisDialogComponent, CreateOrEditOshAnalysisDialogData> {
    return this.openDialog(AddOshAnalysisDialogComponent, { data });
  }

  public openCreateTaskDialog(
    data: CreateTaskDialogData,
    injector?: Injector
  ): MatDialogRef<CreateTaskDialogComponent, CreateTaskModalResultData> {
    const isMobile = this.deviceService.isMobile();

    return this.openDialog(CreateTaskDialogComponent, {
      data,
      injector,
      minWidth: isMobile ? '95dvw' : '700px',
      maxHeight: isMobile ? '95dvh' : 'auto'
    });
  }

  public openCopyTaskDialog(data: CopyTaskDialogData): MatDialogRef<CopyTaskDialogComponent, CopyTaskDialogResult> {
    return this.openDialog(CopyTaskDialogComponent, {
      data,
      width: this.deviceService.isMobile() ? '95vw' : '500px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '500px'
    });
  }

  public openExportTasksDialog(): MatDialogRef<ExportTasksDialogComponent, ExportTasksDialogResult> {
    return this.openDialog(ExportTasksDialogComponent, {
      autoFocus: 'dialog',
      width: this.deviceService.isMobile() ? '95vw' : '480px',
      maxWidth: this.deviceService.isMobile() ? '95vw' : '480px'
    });
  }

  public openTaskAdvancedFiltersDialog(injector?: Injector): MatDialogRef<TaskAdvancedFiltersDialogComponent, void> {
    return this.openDialog(TaskAdvancedFiltersDialogComponent, {
      autoFocus: 'dialog',
      injector,
      width: '95vw',
      maxWidth: '95vw',
      maxHeight: '95vh'
    });
  }

  public openCarouselDialog(data: CarouselDialogData): MatDialogRef<CarouselDialogComponent, void> {
    return this.filePreviewDialogService.open(data);
  }

  private shouldOpenManualHoursTimeReportDialog(timeReportType: TimeReportType): boolean {
    return (
      timeReportType === 'WorkTime' &&
      this.timeReportsActionsService.isTimeReportAdmin(this.authService.currentUser()?.email)
    );
  }

  private openDialog<T, R>(component: ComponentType<T>, config?: MatDialogConfig): MatDialogRef<T, R> {
    return openTrackedDialog<T, R>(this.matDialog, this.destroyRef, component, config);
  }
}
